/**
 * ============================================================================
 * EVIDENCE UPLOAD COMPONENT - 100% Ready with Drag & Drop
 * ============================================================================
 */

import React, { useState, useRef } from 'react';
import { useStorage } from '../../hooks/useStorage';
import { useToast } from '../../providers';
import Button from '../common/Button';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import ProgressBar from '../common/ProgressBar';

/**
 * Evidence Upload Component
 * @param {Object} props
 * @param {Array} props.files - Current files
 * @param {Function} props.onChange - Change handler
 * @returns {JSX.Element}
 */
const EvidenceUpload = ({ files = [], onChange, maxFiles = 10 }) => {
  const { uploadFile, loading } = useStorage();
  const { showError, showSuccess } = useToast();
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const handleFiles = async (fileList) => {
    const selectedFiles = Array.from(fileList);
    
    if (selectedFiles.length === 0) return;

    // Check file count limit
    if (files.length + selectedFiles.length > maxFiles) {
      showError(`Maximum ${maxFiles} files allowed. You already have ${files.length} files.`);
      return;
    }

    setUploading(true);
    setUploadProgress({});

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const fileId = `file-${Date.now()}-${index}`;
        setUploadProgress((prev) => ({ ...prev, [fileId]: 0 }));

        try {
          const uploaded = await uploadFile(file, {
            folder: 'evidence',
            publicId: `evidence_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            onUploadProgress: (progress) => {
              setUploadProgress((prev) => ({ ...prev, [fileId]: progress }));
            },
          });

          setUploadProgress((prev) => ({ ...prev, [fileId]: 100 }));

          return {
            id: fileId,
            name: file.name,
            url: uploaded.url,
            type: file.type,
            size: file.size,
          };
        } catch (error) {
          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
          throw error;
        }
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      onChange([...files, ...uploadedFiles]);
      showSuccess(`${uploadedFiles.length} file(s) uploaded successfully`);
    } catch (error) {
      showError(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  const handleFileSelect = (e) => {
    handleFiles(e.target.files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleRemove = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    onChange(newFiles);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return '🖼️';
    if (type?.includes('pdf')) return '📄';
    if (type?.includes('word') || type?.includes('document')) return '📝';
    return '📎';
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
        Upload Evidence ({files.length}/{maxFiles} files)
      </label>
      
      {/* Drag & Drop Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          relative
          flex items-center justify-center
          w-full h-40
          border-2 border-dashed rounded-xl
          cursor-pointer
          transition-all duration-200
          ${
            dragActive
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-105'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
          }
          ${uploading || loading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onClick={() => !uploading && !loading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.txt"
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading || loading}
        />
        <div className="text-center">
          {uploading ? (
            <>
              <Spinner size="md" className="mb-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400 block">
                Uploading...
              </span>
            </>
          ) : (
            <>
              <span className="text-4xl mb-2 block animate-bounce">📎</span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                Drag & drop files here
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                or click to browse
              </span>
            </>
          )}
        </div>
      </div>

      {/* Uploaded Files */}
      {files.length > 0 && (
        <div className="mt-4 space-y-3">
          {files.map((file, index) => {
            const isUploading = uploadProgress[file.id] !== undefined && uploadProgress[file.id] < 100;
            const progress = uploadProgress[file.id] || 100;

            return (
              <Card
                key={file.id || index}
                variant="outlined"
                hover
                className="animate-fadeInUp"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getFileIcon(file.type)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {file.name}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {file.size && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatFileSize(file.size)}
                        </span>
                      )}
                      {isUploading && (
                        <div className="flex-1 max-w-[200px]">
                          <ProgressBar value={progress} size="sm" variant="info" />
                        </div>
                      )}
                    </div>
                  </div>
                  {!isUploading && (
                    <button
                      onClick={() => handleRemove(index)}
                      className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      aria-label="Remove file"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Info */}
      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        Supported: Images, PDF, Word documents. Max {maxFiles} files, 10MB each.
      </p>
    </div>
  );
};

export default EvidenceUpload;
